import {
  CreateProjectInput,
  Project,
  ProjectVersion,
  UpdateProjectVersionInput,
  UpdateProjectInput,
  FormDataByDateQuery,
  DynamicThumbnail,
  ModelSortDirection,
  DynamicThumbnailInput,
  ExperimentOptimizationTechnique,
  CreateProjectVersionInput,
  OverlayInput,
  FontInput,
  FlowDiagramInput,
} from "../API";
import * as CustomAPITypes from "../graphql/customAPI";
import { generateClient } from "aws-amplify/api";
import {
  createProject,
  createProjectVersion,
  deleteProject,
  updateProject,
  updateProjectVersion,
} from "../graphql/mutations";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import { getProjectVersion, formDataByDate } from "../graphql/queries";
import { ProjectBasicInfo } from "../stores/project/projectsModel";
import {
  customListProjectsBasicInfoByAccount,
  customGetProject,
} from "../graphql/customQueries";
import { getAccessTokenForLambdaAuth } from "./authHelpers";

// Graphql Client
const client = generateClient();

export async function createVersion(project: Project | ProjectVersion) {
  const modifiedCreateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(
        insertIntoGraphQlString(
          insertIntoGraphQlString(
            insertIntoGraphQlString(createProjectVersion, {
              path: ["videoParts"],
              key: "modsArr",
              value: {
                id: true,
                dataStr: true,
                origin: true,
                name: true,
                description: true,
              },
            }),
            {
              path: ["videoParts"],
              key: "fonts",
              value: {
                family: true,
                style: true,
                url: true,
                weight: true,
              },
            }
          ),
          {
            path: ["overlay"],
            key: "audio",
            value: {
              url: true,
              volume: true,
              loop: true,
            },
          }
        ),
        {
          path: [],
          key: "fontOverrides",
          value: {
            family: true,
            style: true,
            url: true,
            weight: true,
          },
        }
      ),
      {
        path: ["flowDiagram"],
        key: "nodes",
        value: {
          id: true,
          label: true,
          data: true,
          lineupId: true,
          type: true,
        },
      }
    ),
    {
      path: ["flowDiagram"],
      key: "edges",
      value: { source: true, target: true, sourceHandle: true, index: true },
    }
  );

  const cleanFlowDiagram = {
    nodes:
      project.flowDiagram?.nodes.map(({ __typename, ...rest }) => rest) || [],
    edges:
      project.flowDiagram?.edges.map(({ __typename, ...rest }) => rest) || [],
  };
  const cleanOverlay = {
    ...project.overlay,
    audio: project.overlay?.audio?.map(({ __typename, ...rest }) => rest) ?? [],
  };

  delete cleanOverlay.__typename;

  const cleanFontOverrides = project.fontOverrides?.map(
    ({ __typename, ...rest }) => rest
  );

  const input: CreateProjectVersionInput = {
    id: project.id,
    accountOwner: isProjectVersion(project)
      ? project.accountOwner
      : project.account.id,
    settings: project.settings,
    settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    stateJsonSchemaStr: project.stateJsonSchemaStr,
    playerVersionToUse: project.playerVersionToUse,
    videoParts: project.videoParts?.map((vp) => {
      const { __typename, ...rest } = vp;
      return rest;
    }),
    flowDiagram: cleanFlowDiagram,
    fontOverrides: cleanFontOverrides,
    overlay: cleanOverlay,
  };

  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { createProjectVersion: projectVersion },
  } = (await client.graphql({
    query: modifiedCreateProjectVersion,
    variables: { input },
    authMode: "lambda",
    authToken,
  })) as { data: { createProjectVersion: ProjectVersion } };
  return projectVersion;
}

export async function createPublished(cpi: CreateProjectInput) {
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { createProject: project },
  } = (await client.graphql({
    query: createProject,
    variables: { input: cpi },
    authMode: "lambda",
    authToken,
  })) as { data: { createProject: Project } };
  return project;
}

export async function updateVersion(project: UpdateProjectVersionInput) {
  const input = {
    id: project.id,
    playerVersionToUse: project.playerVersionToUse,
    experiments: project.experiments,
    ...(project?.settings && { settings: project.settings }),
    ...(project?.settingsJsonSchemaStr && {
      settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    }),
    ...(project?.stateJsonSchemaStr && {
      stateJsonSchemaStr: project.stateJsonSchemaStr,
    }),
    ...(project?.videoParts && { videoParts: project.videoParts }),
  };
  const modifiedUpdateProject = insertIntoGraphQlString(
    insertIntoGraphQlString(updateProjectVersion, {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description: true,
      },
    }),
    {
      path: ["videoParts"],
      key: "fonts",
      value: {
        family: true,
        style: true,
        url: true,
        weight: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  return (await client.graphql({
    query: modifiedUpdateProject,
    variables: { input },
    authMode: "lambda",
    authToken,
  })) as { data: ProjectVersion };
}

export const savePublished = async (cpi: UpdateProjectInput) => {
  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: updateProject,
    variables: { input: cpi },
    authMode: "lambda",
    authToken,
  });
};

export const deleteProjectVersionAndPublished = async (id: string) => {
  const deleteProjectVersionGraphQL = `mutation DeleteProjectVersion(
  $input: DeleteProjectVersionInput!
  $condition: ModelProjectVersionConditionInput
) {
  deleteProjectVersion(input: $input, condition: $condition) {
    id
  }
}
`;
  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: deleteProjectVersionGraphQL,
    variables: { input: { id } },
    authMode: "lambda",
    authToken,
  });
  await client.graphql({
    query: deleteProject,
    variables: { input: { id } },
    authMode: "lambda",
    authToken,
  });
};
export async function publishProject(projectVersion: UpdateProjectInput) {
  const {
    id,
    settings,
    settingsJsonSchemaStr,
    stateJsonSchemaStr,
    playerVersionToUse,
    videoParts,
    experiments,
    textToVideoScript,
    overlay,
    fontOverrides,
  } = projectVersion;
  const input = {
    id,
    settings,
    experiments,
    playerVersionToUse,
    settingsJsonSchemaStr,
    stateJsonSchemaStr,
    videoParts,
    textToVideoScript,
    overlay,
    fontOverrides,
  };

  const modifiedUpdateProject = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(updateProject, {
        path: ["videoParts"],
        key: "modsArr",
        value: {
          id: true,
          dataStr: true,
          origin: true,
          name: true,
          description: true,
        },
      }),
      {
        path: ["videoParts"],
        key: "fonts",
        value: {
          family: true,
          style: true,
          url: true,
          weight: true,
        },
      }
    ),
    {
      path: ["overlay"],
      key: "audio",
      value: {
        url: true,
        volume: true,
        loop: true,
      },
    }
  ).replace("__typename", "");

  const authToken = await getAccessTokenForLambdaAuth();
  return (await client.graphql({
    query: modifiedUpdateProject,
    variables: { input },
    authMode: "lambda",
    authToken,
  })) as {
    data: Project;
  };
}

export async function changeProjectExperimentOptimizationTechnique(
  projectId: string,
  experimentOptimizationTechnique: ExperimentOptimizationTechnique
) {
  const authToken = await getAccessTokenForLambdaAuth();
  const { data } = await client.graphql({
    query: updateProject,
    variables: {
      input: {
        id: projectId,
        experimentOptimizationTechnique,
      },
    },
    authMode: "lambda",
    authToken,
  });
  return data;
}

export async function getPublishedProject(id: string) {
  const modifiedGetProject = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(
        insertIntoGraphQlString(
          insertIntoGraphQlString(
            insertIntoGraphQlString(customGetProject, {
              path: ["videoParts"],
              key: "modsArr",
              value: {
                id: true,
                dataStr: true,
                origin: true,
                name: true,
                description: true,
              },
            }),
            {
              path: ["videoParts"],
              key: "fonts",
              value: {
                family: true,
                style: true,
                url: true,
                weight: true,
              },
            }
          ),
          {
            path: ["experiments"],
            key: "variants",
            value: {
              id: true,
              value: true,
              successFactors: {
                conversionSuccessData: true,
                engagementSuccessData: true,
                selectionCount: true,
                watchTimeSuccessData: true,
              },
            },
          }
        ),
        {
          path: ["flowDiagram"],
          key: "nodes",
          value: {
            id: true,
            label: true,
            data: true,
            lineupId: true,
            type: true,
          },
        }
      ),
      {
        path: ["flowDiagram"],
        key: "edges",
        value: { source: true, target: true, sourceHandle: true, index: true },
      }
    ),
    {
      path: ["overlay"],
      key: "audio",
      value: {
        url: true,
        volume: true,
        loop: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: modifiedGetProject,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: Project } };
  return project;
}

export async function getPublishedProjectBasicInfo(id: string) {
  const getProject = `
  query GetProject($id: ID!) {
    getProject(id: $id) {
      id
      title
      updatedAt
      createdAt
      projectAccountId
      description
      thumbS3Url
    }
  }
`;

  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: getProject,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: ProjectBasicInfo } };

  return project;
}

export async function getLatestProjectVersion(id: string) {
  // TODO add logic for fetching latest version when we have multiple versions per project id
  const modifiedGetProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(
        insertIntoGraphQlString(
          insertIntoGraphQlString(
            insertIntoGraphQlString(getProjectVersion, {
              path: ["videoParts"],
              key: "modsArr",
              value: {
                id: true,
                dataStr: true,
                origin: true,
                name: true,
                description: true,
              },
            }),
            {
              path: ["videoParts"],
              key: "fonts",
              value: {
                family: true,
                style: true,
                url: true,
                weight: true,
              },
            }
          ),
          {
            path: ["experiments"],
            key: "variants",
            value: {
              id: true,
              value: true,
              successFactors: {
                conversionSuccessData: true,
                engagementSuccessData: true,
                selectionCount: true,
                watchTimeSuccessData: true,
              },
            },
          }
        ),
        {
          path: [],
          key: "flowDiagram",
          value: {
            nodes: {
              id: true,
              label: true,
              data: true,
              lineupId: true,
              type: true,
            },
            edges: {
              source: true,
              target: true,
              sourceHandle: true,
              index: true,
            },
          },
        }
      ),
      {
        path: [],
        key: "fontOverrides",
        value: {
          family: true,
          style: true,
          url: true,
          weight: true,
        },
      }
    ),
    {
      path: ["overlay"],
      key: "audio",
      value: {
        url: true,
        volume: true,
        loop: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProjectVersion: projectVersion },
  } = (await client.graphql({
    query: modifiedGetProjectVersion,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProjectVersion: ProjectVersion | null } };
  return projectVersion;
}
export async function hasFormData(projectId: string) {
  const authToken = await getAccessTokenForLambdaAuth();
  let nextToken = null;
  let itemsCount = 0;
  try {
    do {
      const { data } = (await client.graphql({
        query: formDataByDate,
        variables: { projectFormDataId: projectId, limit: 1, nextToken },
        authMode: "lambda",
        authToken,
      })) as { data: FormDataByDateQuery };

      itemsCount = data.formDataByDate?.items?.length || 0;
      nextToken = data.formDataByDate?.nextToken;
    } while (itemsCount === 0 && nextToken);

    return itemsCount > 0 || false;
  } catch (e) {
    return false;
  }
}
export async function getProjectFormData(
  projectId: string,
  fromDate?: Date,
  toDate?: Date,
  nextToken?: string | null,
  pageSize = 25
) {
  const authToken = await getAccessTokenForLambdaAuth();
  const fromDateString = fromDate?.toISOString() || null;
  const toDateString = toDate?.toISOString() || null;
  const { data: formDataListResponse } = (await client.graphql({
    query: formDataByDate,
    variables: {
      projectFormDataId: projectId,
      createdAt: {
        between: [fromDateString, toDateString],
      },
      limit: pageSize,
      nextToken: nextToken || null,
      sortDirection: ModelSortDirection.DESC,
    },
    authMode: "lambda",
    authToken,
  })) as { data: FormDataByDateQuery };

  const { items, nextToken: nextTokenResponse } =
    formDataListResponse.formDataByDate || {};

  return {
    items: items || [],
    nextToken: nextTokenResponse || null,
  };
}
function isProjectVersion(
  project: Project | ProjectVersion
): project is ProjectVersion {
  return (project as ProjectVersion).accountOwner !== undefined;
}
export const saveDynamicThumbnails = async (
  projectId: string,
  dynamicThumbnails: Array<Omit<DynamicThumbnail, "__typename"> | null>
) => {
  const authToken = await getAccessTokenForLambdaAuth();
  const _dynamicThumbs = dynamicThumbnails as DynamicThumbnailInput[];
  await client.graphql({
    query: updateProject,
    variables: {
      input: {
        id: projectId,
        dynamicThumbnails: _dynamicThumbs,
      },
    },
    authMode: "lambda",
    authToken,
  });
};
export const listProjectBasicInfo = async () => {
  const listProjectBasicInfoQuery = `query ListProjects(
    $filter: ModelProjectFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProjects(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        updatedAt
        projectAccountId
        createdAt
        thumbS3Url
      }
      nextToken
    }
  }`;
  const authToken = await getAccessTokenForLambdaAuth();
  let nextToken = null;
  const projects: ProjectBasicInfo[] = [];
  do {
    const projectsData = (await client.graphql({
      query: listProjectBasicInfoQuery,
      variables: { nextToken },
      authMode: "lambda",
      authToken,
    })) as { data: any };
    nextToken = projectsData?.data?.listProjects?.nextToken;
    if (projectsData?.data?.listProjects?.items) {
      projects.push(...projectsData.data.listProjects.items);
    }
  } while (nextToken);
  return projects;
};

export const listProjectsBasicInfoByAccount = async (
  accountId: string,
  addProjects?: any
) => {
  const authToken = await getAccessTokenForLambdaAuth();
  let nextToken = null;
  const projects: ProjectBasicInfo[] = [];
  try {
    do {
      const projectsData = (await client.graphql({
        query: customListProjectsBasicInfoByAccount,
        variables: {
          nextToken,
          projectAccountId: accountId,
          limit: 1000,
          sortDirection: ModelSortDirection.DESC,
          filter: {
            or: [
              { isDeleted: { eq: false } },
              { isDeleted: { attributeExists: false } },
            ],
          },
        },
        authMode: "lambda",
        authToken,
      })) as {
        data: CustomAPITypes.CustomListProjectsBasicInfoByAccountQuery;
        errors?: any[];
      };
      nextToken = projectsData?.data?.listProjectsByAccount?.nextToken;
      if (projectsData?.data?.listProjectsByAccount?.items) {
        const validProjects =
          projectsData.data.listProjectsByAccount.items.filter(
            (p: any) => p && p.id
          ) as ProjectBasicInfo[];
        projects.push(...validProjects);
        if (addProjects) {
          addProjects(validProjects);
        }
      }
    } while (nextToken);
    return projects;
  } catch (e) {
    console.error(e);
  }
};

export const saveOverlay = async (projectId: string, overlay: OverlayInput) => {
  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: updateProject,
    variables: {
      input: {
        id: projectId,
        overlay,
      },
    },
    authMode: "lambda",
    authToken,
  });
};
export const saveOverlayVersion = async (
  projectId: string,
  overlay: OverlayInput
) => {
  const modifiedUpdateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(updateProjectVersion, {
        path: ["overlay"],
        key: "audio",
        value: {
          url: true,
          volume: true,
          loop: true,
        },
      }),
      {
        path: [],
        key: "flowDiagram",
        value: {
          nodes: {
            id: true,
            label: true,
            data: true,
            lineupId: true,
            type: true,
          },
          edges: {
            source: true,
            target: true,
            sourceHandle: true,
            index: true,
          },
        },
      }
    ),
    {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: modifiedUpdateProjectVersion,
    variables: {
      input: {
        id: projectId,
        overlay,
      },
    },
    authMode: "lambda",
    authToken,
  });
};
export const saveFontOverridesDraft = async (
  projectId: string,
  fontOverrides: Array<FontInput>
) => {
  const modifiedUpdateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(updateProjectVersion, {
        path: ["overlay"],
        key: "audio",
        value: {
          url: true,
          volume: true,
          loop: true,
        },
      }),
      {
        path: [],
        key: "flowDiagram",
        value: {
          nodes: {
            id: true,
            label: true,
            data: true,
            lineupId: true,
            type: true,
          },
          edges: {
            source: true,
            target: true,
            sourceHandle: true,
            index: true,
          },
        },
      }
    ),
    {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description: true,
      },
    }
  );

  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: modifiedUpdateProjectVersion,
    variables: {
      input: {
        id: projectId,
        fontOverrides: fontOverrides,
      },
    },
    authMode: "lambda",
    authToken,
  });
};

export const saveFlowDiagramDraft = async (
  projectId: string,
  flowDiagram: FlowDiagramInput
) => {
  const modifiedUpdateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(updateProjectVersion, {
        path: ["overlay"],
        key: "audio",
        value: {
          url: true,
          volume: true,
          loop: true,
        },
      }),
      {
        path: [],
        key: "flowDiagram",
        value: {
          nodes: {
            id: true,
            label: true,
            data: true,
            lineupId: true,
            type: true,
          },
          edges: {
            source: true,
            target: true,
            sourceHandle: true,
            index: true,
          },
        },
      }
    ),
    {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  await client.graphql({
    query: modifiedUpdateProjectVersion,
    variables: {
      input: {
        id: projectId,
        flowDiagram,
      },
    },
    authMode: "lambda",
    authToken,
  });
};

export const saveFlowDiagram = async (
  projectId: string,
  flowDiagram: FlowDiagramInput
) => {
  const authToken = await getAccessTokenForLambdaAuth();
  await client
    .graphql({
      query: updateProject,
      variables: {
        input: {
          id: projectId,
          flowDiagram,
        },
      },
      authMode: "lambda",
      authToken,
    })
    .catch((e) => {
      console.error(e);
    });
};
